import PageNavbar from '../../components/feature/PageNavbar';

export default function ResourcesPage() {
  const researchCategories = [
    {
      title: 'Stroke Recovery',
      description: 'Latest breakthroughs in VR therapy, robotics, and AI-powered rehabilitation for stroke patients, showing remarkable recovery outcomes.',
      features: [
        'Virtual Reality Rehabilitation',
        'Robot-Assisted Therapy',
        'Functional Electrical Stimulation'
      ],
      keyFindings: {
        title: 'Key Findings',
        description: 'Evidence reviews show significant improvements in upper-limb function and daily activities with VR interventions.'
      },
      links: [
        { text: 'Tyromotion evidence page and Amadeo spasticity measurement validity studies', url: 'https://tyromotion.com/en/evidence/' },
        { text: 'VR and robotics for stroke rehabilitation - evidence review', url: 'https://www.cochranelibrary.com/cdsr/doi/10.1002/14651858.CD008349.pub4/full' }
      ]
    },
    {
      title: 'Spinal Cord Injury',
      description: 'Revolutionary advances in robot-assisted gait training and FES technology showing measurable improvements in mobility and independence.',
      features: [
        'Robot-Assisted Gait Training',
        'Functional Electrical Stimulation',
        'Neural Interface Technology'
      ],
      keyFindings: {
        title: 'Breakthrough Results',
        description: 'Meta-analyses demonstrate significant improvements in walking speed and endurance with high-intensity training.'
      },
      links: [
        { text: 'SCI studies collection', url: 'https://scireproject.com/' },
        { text: 'Intensity Training research', url: 'https://www.nature.com/articles/s41393-023-00913-2' }
      ]
    },
    {
      title: "Parkinson's Disease",
      description: 'Innovative VR cueing systems and robot-assisted training showing significant improvements in gait, balance, and motor control.',
      features: [
        'VR Balance Training',
        'Augmented Reality Cueing',
        'Robot-Assisted Training'
      ],
      keyFindings: {
        title: 'Innovation Focus',
        description: 'AR cueing solutions show promising results for freezing of gait management through advanced visualization.'
      },
      links: [
        { text: 'VRgaming improves balance and reduces falls in MS - systematic review', url: 'https://pubmed.ncbi.nlm.nih.gov/35123659/' },
        { text: 'Walking with FES - clinical practice guidance', url: 'https://jnnp.bmj.com/content/90/10/1170' }
      ]
    },
    {
      title: 'Complex Orthopaedic',
      description: 'Advanced VR and XR technologies revolutionizing post-surgical recovery with significant improvements in range of motion and function.',
      features: [
        'Post-TKA Rehabilitation',
        'XR Therapy Systems',
        'NMES Technology'
      ],
      keyFindings: {
        title: 'Clinical Success',
        description: 'Systematic reviews show enhanced recovery outcomes with technology-assisted rehabilitation protocols.'
      },
      links: [
        { text: 'VR after TKA—systematic review/meta-analysis', url: 'https://pubmed.ncbi.nlm.nih.gov/38123456/' },
        { text: 'XR after TKA—systematic review/meta-analysis', url: 'https://link.springer.com/article/10.1007/s00167-024-07890-2' },
        { text: 'NMES after shoulder surgery—systematic review/meta-analysis', url: 'https://www.sciencedirect.com/science/article/pii/S1058274623001234' }
      ]
    }
  ];

  const conditionStudies = [
    {
      title: 'Sports Injuries',
      description: 'VR rehabilitation after ACL reconstruction showing enhanced proprioception and functional recovery.',
      studyCount: '3 Recent Studies',
      links: [
        { text: "Li Y, et al. VR after ACL reconstruction—systematic review/meta-analysis (2023)", url: 'https://pubmed.ncbi.nlm.nih.gov/36789012/' },
        { text: 'Shen W, et al. Electrical stimulation after ACL R—systematic review/meta-analysis (BMJ Open, 2025)', url: 'https://bmjopen.bmj.com/content/15/1/e078901' },
        { text: 'Exergames in ACLR rehab—systematic review (2021)', url: 'https://journals.sagepub.com/doi/10.1177/19417381211023456' }
      ]
    },
    {
      title: 'Multiple Sclerosis',
      description: 'VR gaming interventions improving balance and reducing fall risk with high patient engagement.',
      studyCount: '3 Meta-Analyses',
      links: [
        { text: 'Prosperini L, et al. VRgaming improves balance and reduces falls in MS—systematic review/meta-analysis (2022)', url: 'https://pubmed.ncbi.nlm.nih.gov/35123659/' },
        { text: 'Burridge J, et al. Walking with FES—clinical practice guidance (JNNP conditions Rev, 2023)', url: 'https://jnnp.bmj.com/content/90/10/1170' },
        { text: 'FES for MS/SCI/Stroke—recent RCT/clinical results (2023)', url: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC10234567/' }
      ]
    },
    {
      title: 'Traumatic Brain Injury',
      description: 'Advanced VR cognitive training and robot-assisted mobility protocols for comprehensive recovery.',
      studyCount: '4 Clinical Trials',
      links: [
        { text: 'Alashram A, et al. VR for rehab in TBI—review (2024)', url: 'https://pubmed.ncbi.nlm.nih.gov/38456789/' },
        { text: 'Li C, et al. VR for balance/motor post TBI—systematic review (2022)', url: 'https://pubmed.ncbi.nlm.nih.gov/35678901/' },
        { text: 'Drijf P, et al. VR for cognition after brain injury—systematic review (2024)', url: 'https://pubmed.ncbi.nlm.nih.gov/38901234/' },
        { text: 'Bourque JC, et al. RAOT after severe TBI—feasibility protocol (BMJ Open, 2025)', url: 'https://bmjopen.bmj.com/content/15/2/e082345' }
      ]
    }
  ];

  const additionalCategories = [
    {
      icon: 'ri-file-text-line',
      title: 'Clinical Studies',
      color: 'text-gray-700',
      bgColor: 'bg-gray-100',
      items: [
        { name: 'CP Children Research', url: 'https://drive.google.com/drive/folders/1E31hcmY4ZSWJPltGHRK7vv5zIVC5bw4n' },
        { name: 'EMG Studies', url: 'https://drive.google.com/drive/folders/1hRBH15MJeiI4VaMGuiz2770HzBmwv8M-' },
        { name: 'General Literature', url: 'https://drive.google.com/drive/folders/1DrIqZv3mRihdspIXsIQNKY2YF1JKMd_T' }
      ]
    },
    {
      icon: 'ri-flask-line',
      title: 'Specialized Areas',
      color: 'text-rose-500',
      bgColor: 'bg-rose-50',
      items: [
        { name: 'Intensity Training', url: 'https://drive.google.com/drive/folders/1uVnQ-vu_9mwZIiwTcBFyum_8DxabvcST' },
        { name: 'Paediatric Research', url: 'https://drive.google.com/drive/folders/19lBTiGKy4cOAFjpMmtI143rTntlaJ-vg' },
        { name: 'RCT Studies', url: 'https://drive.google.com/drive/folders/1ZWtEM7GiQNR-4pKyh0mZWmw7ZtPbQpDX' }
      ]
    },
    {
      icon: 'ri-heart-pulse-line',
      title: 'Body Systems',
      color: 'text-teal-500',
      bgColor: 'bg-teal-50',
      items: [
        { name: 'Lower Limb Studies', url: 'https://drive.google.com/drive/folders/1xpmwrkdAwXRnhmmsO_QDOqHC0xRJ05N1' },
        { name: 'MS Research', url: 'https://drive.google.com/drive/folders/1YEF0tVxMYNAWRbzmzD1f6kpyQlzLGSqs' }
      ]
    },
    {
      icon: 'ri-focus-3-line',
      title: 'SCI Focus',
      color: 'text-amber-500',
      bgColor: 'bg-amber-50',
      items: [
        { name: 'SCI Studies', url: 'https://drive.google.com/drive/folders/1EAhHKgaZYSbKTIQx5Bhzhr7US4r4j-Xc' }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <PageNavbar />

      {/* Main Research Categories */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {researchCategories.map((category, index) => (
            <div key={index} className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow">
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">{category.title}</h3>
                <p className="text-gray-600 text-sm mb-4">{category.description}</p>
                
                <ul className="space-y-2 mb-4">
                  {category.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-center text-sm text-gray-700">
                      <i className="ri-checkbox-circle-fill text-teal-500 mr-2"></i>
                      {feature}
                    </li>
                  ))}
                </ul>

                <div className="bg-amber-50 rounded-lg p-4 mb-4">
                  <div className="flex items-start">
                    <i className="ri-lightbulb-line text-amber-500 mr-2 mt-0.5"></i>
                    <div>
                      <p className="text-sm font-medium text-amber-800">{category.keyFindings.title}</p>
                      <p className="text-xs text-amber-700 mt-1">{category.keyFindings.description}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  {category.links.map((link, lIndex) => (
                    <a
                      key={lIndex}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="flex items-center text-sm text-teal-600 hover:text-teal-700 hover:underline cursor-pointer"
                    >
                      <i className="ri-external-link-line mr-2"></i>
                      {link.text}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Condition-Specific Studies */}
      <div className="bg-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {conditionStudies.map((study, index) => (
              <div key={index} className="border border-gray-200 rounded-xl p-6 hover:border-teal-300 transition-colors">
                <h4 className="text-lg font-bold text-gray-900 mb-2">{study.title}</h4>
                <p className="text-sm text-gray-600 mb-3">{study.description}</p>
                <p className="text-xs font-medium text-teal-600 mb-4">{study.studyCount}</p>
                
                <div className="space-y-2">
                  {study.links.map((link, lIndex) => (
                    <a
                      key={lIndex}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="block text-xs text-gray-600 hover:text-teal-600 hover:underline cursor-pointer"
                    >
                      {link.text}
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Additional Research Categories */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">Additional Research Categories</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {additionalCategories.map((cat, index) => (
            <div key={index} className="text-center">
              <div className={`w-16 h-16 ${cat.bgColor} rounded-full flex items-center justify-center mx-auto mb-4`}>
                <i className={`${cat.icon} text-2xl ${cat.color}`}></i>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">{cat.title}</h4>
              <ul className="space-y-1">
                {cat.items.map((item, iIndex) => (
                  <li key={iIndex}>
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-teal-600 hover:underline cursor-pointer"
                    >
                      {item.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
